<template>
  <div class="handle-out">
    <div class="text-h5 mb-4">
      Handle Out
    </div>
    <v-stepper
      v-model="step"
      flat
      class="elevation-1 mb-6"
    >
      <v-stepper-header>
        <template v-for="(label, index) in steps">
          <v-divider
            v-if="index"
            :key="`divider-${index}`"
          />
          <v-stepper-step
            :key="label"
            :step="index + 1"
            :complete="step > index + 1"
          >
            {{ label }}
          </v-stepper-step>
        </template>
      </v-stepper-header>
    </v-stepper>

    <v-card v-if="step === 1">
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            md="4"
          >
            <client-auto-complete
              :client="client"
              :allow-new="false"
              label-text="Select a client"
              clearable
              @onClickClient="clientSelected"
            />
          </v-col>
          <v-col
            cols="12"
            md="5"
          >
            <v-autocomplete
              v-model="subClient"
              :items="subClientOptions"
              :disabled="!client"
              :loading="optionsLoading"
              item-text="label"
              item-value="subClientId"
              return-object
              clearable
              label="Select a sub client"
              no-data-text="No sub clients with items in storage"
            />
          </v-col>
          <v-col
            cols="12"
            md="3"
          >
            <v-btn
              color="primary"
              depressed
              :disabled="!subClient"
              :loading="itemsLoading"
              @click="showItems"
            >
              Show items in storage
            </v-btn>
          </v-col>
        </v-row>
        <p class="text-caption mb-0">
          The count next to each sub client is what is in storage right now.
        </p>
      </v-card-text>
    </v-card>

    <v-card v-if="step === 2">
      <v-card-title class="align-center">
        <v-text-field
          v-model="filter"
          prepend-icon="mdi-magnify"
          label="Filter by ICN, description or manufacturer"
          clearable
          class="item-filter mr-4"
        />
        <span class="text-body-2">
          {{ filteredItems.length }} of {{ items.length }} items shown ·
          {{ formatVolume(storageVolume) }} cu ft in storage · {{ subClient.name }}
        </span>
        <v-spacer />
        <v-btn
          text
          @click="changeSubClient"
        >
          Change sub client
        </v-btn>
      </v-card-title>
      <v-card-text class="text-caption">
        Click an ICN to open the item in a new tab.
      </v-card-text>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredItems"
        :items-per-page="-1"
        item-key="itemId"
        sort-by="icn"
        dense
        show-select
        hide-default-footer
        no-data-text="Nothing in storage for this sub client"
      >
        <template v-slot:item.icn="{ item }">
          <a
            :href="`/inventory/${item.itemId}`"
            target="_blank"
            rel="noopener"
            class="icn"
            @click.stop
          >{{ item.icn }}</a>
        </template>
        <template v-slot:item.description="{ item }">
          <span :class="{ 'grey--text': !description(item) }">
            {{ description(item) || 'no description' }}
          </span>
        </template>
        <template v-slot:item.volume="{ item }">
          {{ formatVolume(item.volume) }}
        </template>
        <template v-slot:item.receivedDate="{ item }">
          {{ formatDate(item.receivedDate) }}
        </template>
      </v-data-table>
    </v-card>

    <div
      v-if="step === 2"
      class="selection-bar elevation-8 pa-4 d-flex align-center flex-wrap"
      :style="{ left: `${$vuetify.application.left}px` }"
    >
      <span class="mr-6">{{ itemCount(selected.length) }} selected</span>
      <div class="mr-6">
        <v-icon color="primary">
          mdi-cube-outline
        </v-icon>
        <strong class="primary--text">{{ formatVolume(selectedVolume) }} cu ft</strong>
        <span class="text-caption ml-1">selected</span>
      </div>
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        top
        offset-y
        max-width="290"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="date"
            label="Handled out date"
            prepend-icon="mdi-calendar"
            readonly
            hide-details
            class="date-field mr-4"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          :max="today"
          @input="dateMenu = false"
        />
      </v-menu>
      <v-spacer />
      <v-btn
        text
        @click="selected = []"
      >
        Clear
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!selected.length"
        @click="confirmDialog = true"
      >
        Handle Out{{ selected.length ? ` (${selected.length})` : '' }}
      </v-btn>
    </div>

    <v-dialog
      v-model="confirmDialog"
      max-width="580"
      :persistent="saving"
    >
      <v-card>
        <v-card-title>Handle out {{ itemCount(selected.length) }}</v-card-title>
        <v-card-subtitle v-if="client && subClient">
          {{ client.name }} · {{ subClient.name }}
        </v-card-subtitle>
        <v-card-text>
          <ul class="confirmation-items mb-4">
            <li
              v-for="item in selected"
              :key="item.itemId"
              class="mb-2"
            >
              <span class="icn">{{ item.icn }}</span> -
              {{ description(item) || 'no description' }}
              ({{ location(item) }}, {{ formatVolume(item.volume) }} cu ft)
            </li>
          </ul>
          <p><strong>Handled out date:</strong> {{ formatDate(date) }}</p>
          <p><strong>Total volume:</strong> {{ formatVolume(selectedVolume) }} cu ft</p>
          <p class="text-caption mb-0">
            Storage charges for these items stop on the date above. The items leave storage
            and the date shows as the Handled Out Date on each item page.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="saving"
            @click="confirmDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            depressed
            :loading="saving"
            @click="handleOut"
          >
            Handle out
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card
      v-if="step === 4"
      class="text-center pa-6"
    >
      <v-chip
        small
        color="green lighten-4"
        class="green--text text--darken-3 mb-2"
      >
        HANDLED OUT
      </v-chip>
      <div class="text-h6">
        {{ itemCount(completed.items.length) }} handled out for {{ subClient.name }}
      </div>
      <p class="grey--text mt-2">
        {{ client.name }} · handled out {{ formatDate(completed.date) }} ·
        {{ remainingCount }} still in storage
      </p>
      <v-row class="my-4">
        <v-col
          v-for="stat in doneStats"
          :key="stat.label"
          cols="12"
          sm="6"
          md="3"
        >
          <v-card
            outlined
            class="pa-4"
          >
            <div class="text-caption">
              {{ stat.label }}
            </div>
            <div class="text-h6">
              {{ stat.value }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-alert
        v-if="reloadFailed"
        type="error"
        text
      >
        Items were handled out, but the remaining inventory could not be loaded.
        <v-btn
          text
          :loading="itemsLoading"
          @click="reloadRemaining"
        >
          Retry loading
        </v-btn>
      </v-alert>
      <div class="d-flex justify-center flex-wrap">
        <v-btn
          color="primary"
          :loading="receiptLoading"
          class="ma-2"
          @click="printReceipt"
        >
          <v-icon left>
            mdi-draw
          </v-icon>
          Print delivery receipt
        </v-btn>
        <v-btn
          text
          class="ma-2"
          :disabled="itemsLoading || reloadFailed"
          @click="handleMore"
        >
          Handle out more from this sub client
        </v-btn>
        <v-btn
          text
          class="ma-2"
          :disabled="itemsLoading"
          @click="startOver"
        >
          Start over
        </v-btn>
      </div>
    </v-card>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="6000"
    >
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import ClientAutoComplete from './ClientAutoComplete.vue';

// Timestamps from the API carry a Central offset; the stored calendar day is the UTC day,
// the same one the item page shows.
function calendarDay(value) {
  return value.length === 10 ? value : new Date(value).toISOString().substr(0, 10);
}

function localToday() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export default {
  name: 'HandleOutComponent',
  components: { ClientAutoComplete },
  data: () => ({
    step: 1,
    steps: [
      'Choose sub client',
      'Pick the items leaving',
      'Confirm handled out date',
      'Done: delivery receipt',
    ],
    client: null,
    subClient: null,
    options: [],
    optionsLoading: false,
    items: [],
    itemsLoading: false,
    selected: [],
    filter: '',
    date: localToday(),
    today: localToday(),
    dateMenu: false,
    confirmDialog: false,
    saving: false,
    receiptLoading: false,
    reloadFailed: false,
    completed: { items: [], date: '', volume: 0 },
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    headers: [
      { text: 'ICN', value: 'icn' },
      { text: 'Description', value: 'description' },
      { text: 'Manufacturer', value: 'manufacturerName' },
      { text: 'Warehouse', value: 'warehouse.name' },
      { text: 'Internal Location', value: 'warehouseLocation.name' },
      { text: 'Cu ft', value: 'volume', align: 'end' },
      { text: 'Received', value: 'receivedDate' },
      { text: 'Days in storage', value: 'daysInStorage', align: 'end' },
    ],
  }),
  computed: {
    subClientOptions() {
      return this.options.filter((option) => option.itemCount > 0).map((option) => ({
        ...option,
        label: `${option.name} (${option.icnAbbreviation}) - ${option.itemCount} in storage`,
      }));
    },
    filteredItems() {
      const query = (this.filter || '').trim().toLowerCase();
      return this.items.filter((item) => [item.icn, item.description, item.manufacturerName]
        .some((value) => (value || '').toLowerCase().includes(query)));
    },
    storageVolume() {
      return this.items.reduce((sum, item) => sum + item.volume, 0);
    },
    selectedVolume() {
      return this.selected.reduce((sum, item) => sum + item.volume, 0);
    },
    remainingCount() {
      return this.itemsLoading || this.reloadFailed ? '...' : this.itemCount(this.items.length);
    },
    doneStats() {
      return [
        { label: 'Items handled out', value: this.completed.items.length },
        { label: 'Handled out date', value: this.formatDate(this.completed.date) },
        { label: 'Total volume', value: `${this.formatVolume(this.completed.volume)} cu ft` },
        { label: 'Still in storage', value: this.remainingCount },
      ];
    },
  },
  methods: {
    ...mapActions('apiClient', ['get', 'post']),
    itemCount(count) {
      return `${count} ${count === 1 ? 'item' : 'items'}`;
    },
    formatVolume(volume) {
      return volume.toLocaleString('en-US', { maximumFractionDigits: 2 });
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(`${calendarDay(value)}T00:00:00`);
      return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    },
    description(item) {
      return (item.description || '').trim();
    },
    location(item) {
      return (item.warehouseLocation && item.warehouseLocation.name) || '-';
    },
    notify(message, color = 'error') {
      this.snackbarText = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    async clientSelected(client) {
      if (client && this.client && client.clientId === this.client.clientId
        && (this.options.length || this.subClient || this.optionsLoading)) return;
      this.client = client;
      this.subClient = null;
      this.options = [];
      if (!client) return;
      this.optionsLoading = true;
      try {
        const response = await this.get({ url: `/clients/${client.clientId}/subClients/options` });
        if (this.client && this.client.clientId === client.clientId) this.options = response.data;
      } catch (error) {
        this.notify('Could not load sub clients. Please select the client again.');
      } finally {
        this.optionsLoading = false;
      }
    },
    async loadItems() {
      const { subClientId } = this.subClient;
      const { clientId } = this.client;
      const response = await this.get({ url: `/inventory/subClient/${subClientId}` });
      // Sub clients can have inventory under more than one parent client.
      return response.data.filter((item) => item.active === true
        && item.client.clientId === clientId).map((item) => ({
        ...item,
        manufacturerName: ((item.manufacturer && item.manufacturer.name) || '').trim() || '-',
        volume: Math.round((item.length / 12) * (item.width / 12) * (item.height / 12) * 4) / 4,
        daysInStorage: Math.max(0, Math.floor((Date.parse(localToday())
          - Date.parse(calendarDay(item.receivedDate))) / 86400000)),
      })).sort((a, b) => a.icn.localeCompare(b.icn));
    },
    async showItems() {
      if (!this.subClient || this.itemsLoading) return;
      this.itemsLoading = true;
      const chosenSubClient = this.subClient.subClientId;
      const chosenClient = this.client.clientId;
      try {
        const items = await this.loadItems();
        if (!this.subClient || this.subClient.subClientId !== chosenSubClient
          || !this.client || this.client.clientId !== chosenClient) return;
        this.items = items;
        this.handleMore();
      } catch (error) {
        this.notify('Could not load items in storage. Please try again.');
      } finally {
        this.itemsLoading = false;
      }
    },
    changeSubClient() {
      this.selected = [];
      this.step = 1;
    },
    async handleOut() {
      if (this.saving || !this.selected.length) return;
      this.saving = true;
      const completed = {
        items: this.selected.slice(), date: this.date, volume: this.selectedVolume,
      };
      try {
        await this.post({
          url: `/inventory/billed?date=${completed.date}`,
          body: { ids: completed.items.map((item) => item.itemId) },
        });
      } catch (error) {
        this.notify('Could not handle out items. Please try again.');
        this.saving = false;
        return;
      }
      this.completed = completed;
      this.confirmDialog = false;
      this.selected = [];
      this.step = 4;
      this.saving = false;
      this.notify(`${this.itemCount(completed.items.length)} handled out`
        + ` · charges stop ${this.formatDate(completed.date)}`, 'success');
      await this.reloadRemaining();
    },
    async reloadRemaining() {
      this.itemsLoading = true;
      this.reloadFailed = false;
      try {
        this.items = await this.loadItems();
        const { subClientId } = this.subClient;
        this.options = this.options.map((option) => (option.subClientId === subClientId
          ? { ...option, itemCount: this.items.length } : option));
      } catch (error) {
        this.reloadFailed = true;
      } finally {
        this.itemsLoading = false;
      }
    },
    handleMore() {
      this.selected = [];
      this.filter = '';
      this.today = localToday();
      this.date = this.today;
      this.step = 2;
    },
    startOver() {
      this.client = null;
      this.subClient = null;
      this.options = [];
      this.items = [];
      this.selected = [];
      this.completed = { items: [], date: '', volume: 0 };
      this.date = localToday();
      this.step = 1;
    },
    async printReceipt() {
      if (this.receiptLoading) return;
      this.receiptLoading = true;
      const { date, items } = this.completed;
      const abbreviation = this.subClient.icnAbbreviation;
      try {
        const response = await this.post({
          url: `/inventory/handle-out/receipt?date=${date}`,
          body: { ids: items.map((item) => item.itemId) },
        });
        const bytes = Uint8Array.from(window.atob(response.data), (char) => char.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `delivery-receipt-${abbreviation}-${date}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        this.notify('Could not download the delivery receipt. Please try again.');
      } finally {
        this.receiptLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.handle-out { padding-bottom: 180px; }
.item-filter { max-width: 420px; }
.icn { font-family: monospace; }
.confirmation-items { max-height: 220px; overflow-y: auto; }
.selection-bar { position: fixed; bottom: 0; right: 0; z-index: 5; background: white; gap: 8px; }
.date-field { max-width: 190px; }
</style>
